import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, User, Receipt, Send, Camera, X } from "lucide-react";
import { BRAND, AMC, waLink } from "@/data/site";
import { Reveal, SectionHead, WaIcon, useLang } from "./common";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const field = "w-full rounded-2xl bg-[#F8F9FA] border border-[#0F295B]/15 px-4 py-3.5 text-[#0F295B] placeholder:text-[#6B7A93] focus:outline-none focus:border-[#00A651] focus:ring-4 focus:ring-[#00A651]/15 transition-[border-color,box-shadow]";
const MAX_PHOTOS = 3;
const MAX_SIZE = 5 * 1024 * 1024;
const OK_TYPES = ["image/jpeg", "image/png", "image/webp"];

const Info = ({ icon: I, label, children, testid }) => (
  <div className="flex gap-4 py-5 border-b border-white/10 last:border-0" data-testid={testid}>
    <span className="w-11 h-11 rounded-full bg-white/10 grid place-items-center shrink-0"><I className="w-5 h-5 text-[#F39C12]" /></span>
    <div><p className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-slate-300">{label}</p><div className="mt-1 text-white font-semibold">{children}</div></div>
  </div>
);

const ContactForm = () => {
  const { t, lang } = useLang();
  const [f, setF] = useState({ name: "", phone: "", location: "", service: 0, capacity: 0, message: "" });
  const [photos, setPhotos] = useState([]);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [photoErr, setPhotoErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onPhotos = (e) => {
    const picked = Array.from(e.target.files || []);
    const bad = [];
    const ok = [];
    for (const p of picked) {
      if (!OK_TYPES.includes(p.type)) { bad.push(p.name); continue; }
      if (p.size > MAX_SIZE) { bad.push(p.name); continue; }
      ok.push(p);
    }
    const next = [...photos, ...ok].slice(0, MAX_PHOTOS);
    setPhotos(next);
    setPhotoErr(bad.length ? `${bad.join(", ")} — ${picked[0] && OK_TYPES.includes(picked[0].type) ? "over 5 MB" : "JPG/PNG/WEBP only"}` : "");
  };
  const removePhoto = (i) => setPhotos(photos.filter((_, j) => j !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    const serviceName = t.contact.services[f.service];
    const capName = f.capacity < AMC.length ? AMC[f.capacity].cap : t.contact.capExtra[f.capacity - AMC.length];
    const text = lang === "hi"
      ? `नमस्ते Vivan Energia,\nनाम: ${f.name}\nफ़ोन: ${f.phone}\nलोकेशन: ${f.location}\nसेवा: ${serviceName}\nप्लांट क्षमता: ${capName}${f.message ? `\nसंदेश: ${f.message}` : ""}`
      : `Hi Vivan Energia,\nName: ${f.name}\nPhone: ${f.phone}\nLocation: ${f.location}\nService: ${serviceName}\nPlant capacity: ${capName}${f.message ? `\nMessage: ${f.message}` : ""}`;
    const paths = [];
    if (photos.length) {
      for (const p of photos) {
        try {
          const fd = new FormData();
          fd.append("file", p);
          const r = await fetch(`${BACKEND}/api/upload`, { method: "POST", body: fd });
          const d = await r.json();
          if (d.path) paths.push(d.path);
        } catch { /* keep going — WhatsApp still opens */ }
      }
    }
    try {
      await fetch(`${BACKEND}/api/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: f.name, phone: f.phone, location: f.location, service: serviceName, capacity: capName, message: f.message, photos: paths, lang }),
      });
      setSent(true);
    } catch { /* lead still goes to WhatsApp */ }
    setSending(false);
    window.open(waLink(text), "_blank", "noopener");
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="rounded-[2rem] bg-white border border-[#0F295B]/10 p-6 sm:p-9 grid sm:grid-cols-2 gap-4">
      <input required className={field} placeholder={t.contact.namePh} value={f.name} onChange={set("name")} data-testid="contact-name" />
      <input required type="tel" pattern="[0-9+\s]{10,15}" className={field} placeholder={t.contact.phonePh} value={f.phone} onChange={set("phone")} data-testid="contact-phone" />
      <input required className={`${field} sm:col-span-2`} placeholder={t.contact.locPh} value={f.location} onChange={set("location")} data-testid="contact-location" />
      <select className={field} value={f.service} onChange={set("service")} data-testid="contact-service">
        {t.contact.services.map((s, i) => <option key={s} value={i}>{s}</option>)}
      </select>
      <select className={field} value={f.capacity} onChange={set("capacity")} data-testid="contact-capacity">
        {AMC.map((p, i) => <option key={p.cap} value={i}>{p.cap}</option>)}
        {t.contact.capExtra.map((c, i) => <option key={c} value={AMC.length + i}>{c}</option>)}
      </select>
      <textarea rows={4} className={`${field} sm:col-span-2 resize-none`} placeholder={t.contact.msgPh} value={f.message} onChange={set("message")} data-testid="contact-message" />
      <div className="sm:col-span-2">
        <label className="flex items-center justify-between gap-3 rounded-2xl border-2 border-dashed border-[#0F295B]/25 px-4 py-4 cursor-pointer hover:border-[#00A651] transition-colors" data-testid="contact-photos">
          <span className="flex items-center gap-3 text-sm font-semibold text-[#0F295B]">
            <span className="w-9 h-9 rounded-full bg-[#00A651]/10 grid place-items-center"><Camera className="w-4 h-4 text-[#00A651]" /></span>
            {t.contact.photosLabel}
          </span>
          <span className="text-[#00A651] font-bold text-sm">+ </span>
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={onPhotos} data-testid="contact-photos-input" />
        </label>
        <p className="text-xs text-[#4A5B78] mt-1.5">{t.contact.photosHint}</p>
        {photos.length > 0 && (
          <div className="flex gap-2 mt-2">
            {photos.map((p, i) => (
              <div key={i} className="relative w-16 h-16 rounded-xl overflow-hidden border border-[#0F295B]/15" data-testid={`photo-preview-${i}`}>
                <img src={URL.createObjectURL(p)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)} aria-label="Remove photo" className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/90 grid place-items-center">
                  <X className="w-3 h-3 text-[#0F295B]" />
                </button>
              </div>
            ))}
          </div>
        )}
        {photoErr && <p data-testid="photo-error" className="text-xs text-red-600 mt-1">{photoErr}</p>}
      </div>
      <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} type="submit" disabled={sending} data-testid="contact-submit"
        className="sm:col-span-2 flex items-center justify-center gap-2 rounded-full bg-[#00A651] text-white font-bold py-4 hover:bg-[#008842] transition-colors disabled:opacity-60">
        <WaIcon /> {sending ? t.contact.uploading : t.contact.submit} <Send className="w-4 h-4" />
      </motion.button>
      <p className="sm:col-span-2 text-xs text-[#4A5B78] text-center" data-testid={sent ? "contact-lead-saved" : undefined}>
        {sent ? "✓ " : ""}{t.contact.note}
      </p>
    </form>
  );
};

export const Contact = () => {
  const { t } = useLang();
  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <SectionHead num={t.contact.num} kicker={t.contact.kicker} segs={t.contact.title} desc={t.contact.desc} testid="contact-head" />
        <div className="grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-5 rounded-[2rem] bg-[#0F295B] grain relative overflow-hidden p-6 sm:p-9">
            <div className="relative">
              <Info icon={User} label={t.contact.proprietor} testid="contact-proprietor">{BRAND.proprietor}</Info>
              <Info icon={Phone} label={t.contact.phoneLabel} testid="contact-phones">
                {BRAND.phones.map((p) => <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="block hover:text-[#F39C12] transition-colors">{p}</a>)}
              </Info>
              <Info icon={Mail} label={t.contact.emailLabel} testid="contact-email"><a href={`mailto:${BRAND.email}`} className="break-all hover:text-[#F39C12] transition-colors">{BRAND.email}</a></Info>
              <Info icon={MapPin} label={t.contact.addressLabel} testid="contact-address">{BRAND.address}</Info>
              <Info icon={Receipt} label={t.contact.gstinLabel} testid="contact-gstin"><span className="font-mono2">{BRAND.gstin}</span></Info>
              <a href={waLink()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="mt-6 flex items-center justify-center gap-2 rounded-full bg-[#00A651] text-white font-bold py-4 hover:bg-[#008842] transition-colors">
                <WaIcon /> {t.contact.waBtn}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7"><ContactForm /></Reveal>
        </div>
      </div>
    </section>
  );
};
